import { createElement, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type PageMenuProps = {
  pageId: string;
  onCopyMarkdown: () => void | Promise<void>;
  onExportMarkdown: () => void | Promise<void>;
  disabled?: boolean;
};

function MenuItem({
  label,
  onPress,
  disabled,
}: {
  label: string;
  onPress: () => void;
  disabled?: boolean;
}) {
  return createElement(
    'button',
    {
      type: 'button',
      className:
        'w-full px-3 py-1.5 text-left text-sm text-fg hover:bg-surface-raised disabled:cursor-not-allowed disabled:opacity-50',
      disabled,
      onClick: (event: MouseEvent) => {
        event.stopPropagation();
        onPress();
      },
    },
    label,
  );
}

export function PageMenu({
  pageId,
  onCopyMarkdown,
  onExportMarkdown,
  disabled = false,
}: PageMenuProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) {
      return;
    }
    const rect = buttonRef.current.getBoundingClientRect();
    const menuWidth = 220;
    const menuHeight = 96;
    setPosition({
      x: Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8)),
      y: Math.min(rect.bottom + 4, window.innerHeight - menuHeight - 8),
    });
  }, [open]);

  useEffect(() => {
    setOpen(false);
    setBusy(false);
  }, [pageId]);

  useEffect(() => {
    if (!open) {
      return;
    }
    function handlePointerDown(event: MouseEvent): void {
      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-page-menu="true"]')) {
        return;
      }
      setOpen(false);
    }
    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  async function runAction(action: () => void | Promise<void>): Promise<void> {
    if (disabled || busy) {
      return;
    }
    setBusy(true);
    try {
      await action();
      setOpen(false);
    } finally {
      setBusy(false);
    }
  }

  return createElement(
    'div',
    { className: 'relative shrink-0', 'data-page-menu': 'true' },
    createElement(
      'button',
      {
        ref: buttonRef,
        type: 'button',
        'aria-label': 'Page actions',
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        className:
          'flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-surface-raised disabled:cursor-not-allowed disabled:opacity-50',
        disabled: disabled || busy,
        onClick: (event: MouseEvent) => {
          event.stopPropagation();
          setOpen((value) => !value);
        },
      },
      createElement(
        'svg',
        {
          xmlns: 'http://www.w3.org/2000/svg',
          viewBox: '0 0 24 24',
          width: 18,
          height: 18,
          fill: 'currentColor',
          'aria-hidden': true,
        },
        createElement('circle', { cx: 5, cy: 12, r: 2 }),
        createElement('circle', { cx: 12, cy: 12, r: 2 }),
        createElement('circle', { cx: 19, cy: 12, r: 2 }),
      ),
    ),
    open && typeof document !== 'undefined'
      ? createPortal(
          createElement(
            'div',
            {
              'data-page-menu': 'true',
              className:
                'fixed min-w-[220px] overflow-hidden rounded-lg border border-border bg-surface-overlay py-1 shadow-xl',
              style: { left: position.x, top: position.y, zIndex: 99999 },
              onMouseDown: (event: MouseEvent) => event.stopPropagation(),
            },
            MenuItem({
              label: busy ? 'Copying…' : 'Copy as Markdown',
              disabled: disabled || busy,
              onPress: () => void runAction(onCopyMarkdown),
            }),
            MenuItem({
              label: busy ? 'Exporting…' : 'Export Markdown',
              disabled: disabled || busy,
              onPress: () => void runAction(onExportMarkdown),
            }),
          ),
          document.body,
        )
      : null,
  );
}
